import { Component, Prop, Watch } from "vue-property-decorator";
import Vue from "vue";
import Vuetify from "vuetify";
import { Option } from "../../../models/Option";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import {
  LIST_CONSTANTS,
  OPTIONS_EMITS
} from "../../../constants/ListConstants";
import {
  FORM_CONSTANTS,
  FORM_VALIDATORS
} from "@/shared/constants/FormConstants";
import { Icon } from "@/shared/models/Icon";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import { PreventKey } from "@/shared/mixins/mixins";
import DynamicSelect from "@/shared/components/DynamicSelect/DynamicSelect.vue";
import { Utils } from "@/shared/utils/utils";

Vue.use(Vuetify);
@Component({
  name: "AddOptions",
  components: {
    DynamicSelect
  }
})
export default class AddOptions extends PreventKey {
  @Prop() public options!: Option[];
  @Prop({ default: false }) private dialogOptions?: boolean;
  @Prop({ default: false }) private isEditing?: boolean;
  @Prop() private indexEditing!: number;
  @Prop() private iconsList!: Icon[];
  @Prop() private optionIsSubCategory!: boolean;

  show = false;
  title = "";
  value = "";
  score = "";
  id = "";
  enabled = true;
  formOption = false;
  formUpdate = false;
  showMoreInfo = false;

  btnClassPrimary = STYLE_CLASSES.PRIMARY_BTN;
  btnRoundClassPrimary = STYLE_CLASSES.PRIMARY_ROUND_BTN;
  btnClassSecondary = STYLE_CLASSES.SECONDARY_BTN;
  cardClass = STYLE_CLASSES.CARD;
  textFieldClass = STYLE_CLASSES.TEXT_FIELD;
  switchClass = STYLE_CLASSES.SWITCH;

  PAGE_TITLE_OPTIONS = LIST_CONSTANTS.ADD_OPTION;
  OPTION_TITLE = LIST_CONSTANTS.LABEL.OPTION_TITLE;
  OPTION_VALUE = LIST_CONSTANTS.LABEL.OPTION_VALUE;
  OPTION_SCORE = LIST_CONSTANTS.LABEL.OPTION_SCORE;
  OPTION_URL_ICON = LIST_CONSTANTS.LABEL.OPTION_URL_ICON;
  OPTION_ACTIVE = FORM_CONSTANTS.LABEL.STATE;
  STATE = LIST_CONSTANTS.LABEL.OPTION_STATE;
  ADD_OPTION = FORM_CONSTANTS.BUTTON.ADD;
  CANCEL = FORM_CONSTANTS.BUTTON.CANCEL;
  MORE_INFO = FORM_CONSTANTS.BUTTON.MORE_INFO;
  OPTIONS_INFO = LIST_CONSTANTS.LABEL.OPTIONS_INFO;

  rules = {
    title: [
      FORM_VALIDATORS.REQUIRED_FIELD,
      LIST_CONSTANTS.LIST_VALIDATORS.MAX_LENGTH,
      this.uniqueNameRule(),
      FORM_VALIDATORS.NOT_BLANK_SPACES_VALIDATOR
    ],
    value: [
      FORM_VALIDATORS.REQUIRED_FIELD,
      FORM_VALIDATORS.NOT_BLANK_SPACES_VALIDATOR
    ],
    icon: [FORM_VALIDATORS.URL_PATTERN]
  };
  formKey = 1;

  iconConfigList: any = {
    multiple: false,
    default: [],
    listAll: this.iconsList,
    messageLabel: this.OPTION_URL_ICON,
    required: false,
    onlyIcon: true
  };
  iconModel = [{ value: "", name: "" }];

  uniqueNameRule(): (value: string) => boolean | string {
    return value =>
      this.validateUniqueName(value) === 0 ||
      LIST_CONSTANTS.RULES_MESSAGE.NAME_EXIST;
  }

  get iconUrl() {
    return this.iconModel[0].value;
  }

  set iconUrl(value: string) {
    const icon = this.iconConfigList.listAll.find(
      (item: { value: string }) => item.value == value
    );
    this.iconModel = [{ value: icon?.value ?? "", name: icon?.name ?? "" }];
  }

  validateUniqueName(title: string): number {
    const optionsNew = this.options.slice();

    if (this.formUpdate) {
      optionsNew.splice(this.indexEditing, 1);
    }

    const result = optionsNew.filter(
      option => option.title.toUpperCase().trim() === title.toUpperCase().trim()
    );
    return result.length;
  }

  addOption() {
    if (this.formOption) {
      const option = {
        score: +this.score,
        id: this.isEditing
          ? this.id
          : Utils.idGenerator() + this.options.length.toString(),
        title: this.title.trim(),
        value: this.value.trim(),
        enabled: this.enabled,

        uiSetup: {
          iconUrl: this.iconUrl
        }
      };

      if (!this.isEditing) {
        const previousOption = this.options.find(
          itemOption => itemOption.value === option.value
        );
        if (previousOption === undefined) {
          this.options.push(option);
        } else {
          Snackbar.popWarning(LIST_CONSTANTS.RULES_MESSAGE.VALUE_EXIST);
          return;
        }
      } else {
        if (this.options[this.indexEditing].value === option.value) {
          this.options[this.indexEditing] = option;
        } else {
          const previousOption = this.options.find(
            itemOption => itemOption.value === option.value
          );
          if (previousOption !== undefined) {
            Snackbar.popWarning(LIST_CONSTANTS.RULES_MESSAGE.VALUE_EXIST);
            return;
          }
          this.options[this.indexEditing] = option;
        }
        this.$emit(OPTIONS_EMITS.UPDATE);
      }
    }

    this.resetForm();
    this.showMoreInfo = false;

    this.$emit(OPTIONS_EMITS.CLOSE);
  }

  cancelOption() {
    this.resetForm();
    this.$emit(OPTIONS_EMITS.CLOSE);
  }

  showFormUpdate(index: number) {
    this.title = this.options[index].title;
    this.value = this.options[index].value;
    this.score = this.options[index].score.toString();
    this.enabled = this.options[index].enabled;
    this.iconUrl = this.options[index].uiSetup.iconUrl;
    this.id = this.options[index].id!;
    this.formUpdate = true;
  }

  @Watch("optionIsSubCategory")
  @Watch("title")
  onPropertyTitleChanged() {
    if (this.optionIsSubCategory) {
      this.value = this.title;
    }
  }

  @Watch("dialogOptions")
  @Watch("indexEditing")
  onPropertyChanged() {
    if (this.isEditing) {
      if (this.dialogOptions) {
        this.showFormUpdate(this.indexEditing);
        this.PAGE_TITLE_OPTIONS = LIST_CONSTANTS.EDIT_OPTION;
        this.ADD_OPTION = FORM_CONSTANTS.BUTTON.EDIT;
      }
    } else {
      this.resetForm();
      this.PAGE_TITLE_OPTIONS = LIST_CONSTANTS.ADD_OPTION;
      this.ADD_OPTION = FORM_CONSTANTS.BUTTON.ADD;
    }
  }

  resetForm() {
    this.title = "";
    this.value = "";
    this.score = "";
    this.enabled = true;
    this.iconUrl = "";
    this.formKey = 1 - this.formKey;
  }
}
